import { useMemo } from 'react';
import type { ChangeEvent } from 'react';

import { Button, Input, PasswordInput } from '@parrot-software/design-system';
import { LoaderCircle, ShieldCheck } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form-v7';
import type { Resolver } from 'react-hook-form-v7';
import { Trans, useTranslation } from 'react-i18next';

import { isPasswordValid } from '@identity/domain/value-objects';
import {
  FieldLabel,
  InfoNote,
  PasswordRequirementsChecklist,
  StepCard,
  VerificationBadge,
} from '@identity/presentation/components/base';
import { useCreatePassword } from '@identity/presentation/hooks';
import { resolveIdentityErrorMessage } from '@identity/presentation/utils';

import { createPasswordFormSchema } from './PasswordStep.schema';
import type { PasswordFormValues } from './PasswordStep.schema';

export type PasswordStepEmailField = {
  /** The account email, shown in a disabled field. It comes from the account status. */
  email: string;
  /** Whether the backend reports the email as met. It is real data, not a constant. */
  isConfirmed: boolean;
};

export type PasswordStepProps = {
  /**
   * The email block — field, badge and note — or nothing at all. One optional object and not two
   * optional props, so an email without its badge flag cannot be expressed.
   *
   * The verification flow leaves it out: the email had its own step there, and repeating it in a
   * disabled field would be ambiguous about whether it can still be changed.
   */
  emailField?: PasswordStepEmailField;
  /**
   * For example «Paso 1 · correo y contraseña», already translated: an i18n key names the flow, and
   * passing one would make the step know which wizard it is in. Absent means the flow does not
   * number its steps.
   */
  stepLabel?: string;
};

/**
 * The password step.
 *
 * It knows nothing about its wizard and does not navigate when it finishes: it saves and stops, and
 * the wizard derives the next step from the updated map. That is what lets both flows reuse it.
 *
 * The field is validated on blur, so nobody is told their password is too short while still typing
 * it, and editing it again clears both the validation error and the one from the last rejected
 * request: an error that outlives the value it described is what makes a form feel stuck.
 *
 * @param {PasswordStepProps} props - Component properties
 */
export const PasswordStep = ({ emailField, stepLabel }: PasswordStepProps) => {
  const { t } = useTranslation('identity');
  const { mutate: createPassword, isLoading, error, reset: resetRequest } = useCreatePassword();
  const resolver = useMemo(
    () => (yupResolver(createPasswordFormSchema()) as unknown) as Resolver<PasswordFormValues>,
    [],
  );

  const {
    clearErrors,
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm<PasswordFormValues>({
    defaultValues: { password: '' },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver,
  });

  const password = watch('password');
  // The same checklist the user sees, so the button enables exactly when the last rule turns green.
  const isSubmitDisabled = isLoading || !isPasswordValid(password);
  const fieldErrorKey = errors.password?.message;
  const requestErrorKey = error ? resolveIdentityErrorMessage(error).i18nKey : undefined;

  const { onChange: onFieldChange, ...passwordField } = register('password');

  /** Typing clears what the previous value was told before registering the new one. */
  const onPasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (fieldErrorKey) clearErrors('password');
    if (error) resetRequest();

    return onFieldChange(event);
  };

  const onSubmit = handleSubmit((values) => createPassword({ password: values.password }));

  return (
    <StepCard
      description={t('password.subtitle')}
      icon={
        <span className="flex size-8 items-center justify-center rounded-full bg-bg-info">
          <ShieldCheck aria-hidden className="size-4 text-info" />
        </span>
      }
      stepLabel={stepLabel}
      title={t('password.title')}
    >
      <form className="flex flex-col gap-6 font-sans" noValidate onSubmit={onSubmit}>
        {emailField && (
          <>
            <div className="flex flex-col gap-1.5">
              <FieldLabel htmlFor="identity-email" isRequired>
                {t('password.emailLabel')}
              </FieldLabel>
              <div className="flex items-center gap-2">
                <Input className="flex-1" disabled id="identity-email" readOnly={false} value={emailField.email} />
                {emailField.isConfirmed && <VerificationBadge isVerified />}
              </div>
            </div>

            <InfoNote>
              <Trans components={{ strong: <strong /> }} i18nKey="identity:password.emailNote" />
            </InfoNote>
          </>
        )}

        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="identity-password" isRequired>
            {t('password.passwordLabel')}
          </FieldLabel>
          <PasswordInput
            {...passwordField}
            aria-invalid={Boolean(fieldErrorKey)}
            aria-required
            autoComplete="new-password"
            className={cn({ 'border-destructive focus-visible:ring-destructive': Boolean(fieldErrorKey) })}
            id="identity-password"
            onChange={onPasswordChange}
          />
          {fieldErrorKey && <p className="text-xs text-destructive">{t(fieldErrorKey)}</p>}
          {requestErrorKey && (
            <p className="text-xs text-destructive" data-testid="password-request-error">
              {t(requestErrorKey)}
            </p>
          )}
        </div>

        <PasswordRequirementsChecklist value={password} />

        <Button className="w-full sm:w-fit" disabled={isSubmitDisabled} type="submit">
          {isLoading && (
            <LoaderCircle aria-hidden className="size-4 shrink-0 animate-spin" data-testid="password-submit-spinner" />
          )}
          {t('password.submit')}
        </Button>
      </form>
    </StepCard>
  );
};
